import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

// Types for the Sparks API
interface SparkBalance {
  balance: number
  lifetime_earned: number
  lifetime_spent: number
}

interface SparkTransaction {
  id: string
  user_id: string
  product: string
  action: string
  sparks_spent?: number
  sparks_added?: number
  balance_after: number
  metadata?: Record<string, any>
  created_at: string
}

export interface SubscriptionAllowance {
  id: string
  plan_id: string
  product_slug: string
  allowance_type: string
  monthly_limit: number
  display_name: string
  display_order: number
}

export interface SubscriptionPlan {
  id: string
  name: string
  tier: 'starter' | 'growth' | 'scale'
  stripe_price_id: string
  stripe_product_id: string
  sparks_per_month: number
  max_rollover: number
  price_cents: number
  currency: string
  features: string[]
  is_active: boolean
  allowances?: SubscriptionAllowance[]
}

export interface SubscriptionUsage {
  product_slug: string
  allowance_type: string
  display_name: string
  monthly_limit: number
  used: number
  remaining: number
  display_order: number
}

interface Subscription {
  id: string
  user_id: string
  organization_id?: string
  stripe_subscription_id: string
  plan_id: string
  status: string
  current_period_start: string
  current_period_end: string
  cancel_at_period_end: boolean
  is_founding_member?: boolean
  plan?: SubscriptionPlan
  usage?: SubscriptionUsage[]
}

interface DashboardData {
  balance: SparkBalance
  subscription?: Subscription
  recent_transactions: SparkTransaction[]
  usage_summary: {
    today: number
    week: number
    month: number
  }
}

export const useSparksStore = defineStore('sparks', () => {
  // State
  const apiUrl = ref<string>('')
  const accessToken = ref<string>('')
  const balance = ref<SparkBalance | null>(null)
  const transactions = ref<SparkTransaction[]>([])
  const activeSubscription = ref<Subscription | null>(null)
  const plans = ref<SubscriptionPlan[]>([])
  const subscriptionUsage = ref<SubscriptionUsage[]>([])
  const dashboardData = ref<DashboardData | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const lastRefresh = ref<Date | null>(null)

  // Auto-refresh
  let refreshTimer: NodeJS.Timeout | null = null
  const autoRefreshInterval = ref(30000) // 30 seconds

  // Computed
  const currentBalance = computed(() => balance.value?.balance ?? 0)
  const lifetimeEarned = computed(() => balance.value?.lifetime_earned ?? 0)
  const lifetimeSpent = computed(() => balance.value?.lifetime_spent ?? 0)
  
  const formattedBalance = computed(() => {
    const bal = currentBalance.value
    return `${bal.toLocaleString()} Spark${bal === 1 ? '' : 's'}`
  })

  const hasActiveSubscription = computed(() => 
    activeSubscription.value?.status === 'active'
  )

  const subscriptionWillCancel = computed(() => 
    activeSubscription.value?.cancel_at_period_end === true
  )

  const recentTransactions = computed(() => 
    transactions.value.slice(0, 10)
  )

  const isInitialized = computed(() => apiUrl.value !== '' && accessToken.value !== '')

  // Actions
  function initializeWithAPI(url: string, token: string) {
    apiUrl.value = url
    accessToken.value = token
  }

  async function refreshBalance() {
    if (!isInitialized.value) throw new Error('Store not initialized')
    
    isLoading.value = true
    error.value = null

    try {
      const response = await fetch(`${apiUrl.value}/sparks/balance`, {
        headers: {
          'Authorization': `Bearer ${accessToken.value}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        if (data.success && data.data) {
          balance.value = data.data
          lastRefresh.value = new Date()
        } else {
          error.value = data.error || 'Failed to fetch balance'
        }
      } else {
        error.value = `Failed to fetch balance: ${response.status}`
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Unknown error'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function refreshTransactions(limit = 50) {
    if (!isInitialized.value) throw new Error('Store not initialized')
    
    try {
      const response = await fetch(`${apiUrl.value}/sparks/ledger?limit=${limit}`, {
        headers: {
          'Authorization': `Bearer ${accessToken.value}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        if (data.success && data.data) {
          transactions.value = data.data
        }
      }
    } catch (err) {
      console.error('Failed to refresh transactions:', err)
    }
  }

  async function refreshSubscription() {
    if (!isInitialized.value) throw new Error('Store not initialized')
    
    try {
      const response = await fetch(`${apiUrl.value}/subscriptions/active`, {
        headers: {
          'Authorization': `Bearer ${accessToken.value}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        if (data.success) {
          activeSubscription.value = data.data
        }
      }
    } catch (err) {
      console.error('Failed to refresh subscription:', err)
    }
  }

  async function refreshDashboard() {
    if (!isInitialized.value) throw new Error('Store not initialized')
    
    try {
      console.log('Calling getDashboardData...');
      const response = await fetch(`${apiUrl.value}/dashboard/user`, {
        headers: {
          'Authorization': `Bearer ${accessToken.value}`,
          'Content-Type': 'application/json'
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        console.log('Dashboard data response:', data);
        
        if (data.success && data.data) {
          dashboardData.value = data.data
          
          // Update individual pieces of state from dashboard data
          balance.value = data.data.spark_balance
          transactions.value = data.data.recent_transactions
          activeSubscription.value = data.data.active_subscription || null
          
          lastRefresh.value = new Date()
          console.log('Dashboard data updated successfully');
        } else {
          console.error('Dashboard data request failed:', data.error);
        }
      } else {
        console.error('Dashboard request failed:', response.status);
      }
    } catch (err) {
      console.error('Failed to refresh dashboard:', err)
      throw err; // Re-throw to propagate error
    }
  }

  async function refreshAll() {
    if (!isInitialized.value) throw new Error('Store not initialized')
    
    isLoading.value = true
    error.value = null

    try {
      await Promise.all([
        refreshBalance(),
        refreshTransactions(),
        refreshSubscription()
      ])
    } finally {
      isLoading.value = false
    }
  }

  async function consumeSparks(options: { product: string; action: string; sparks: number; metadata?: Record<string, any> }) {
    if (!isInitialized.value) throw new Error('Store not initialized')
    
    isLoading.value = true
    error.value = null

    try {
      const response = await fetch(`${apiUrl.value}/sparks/consume`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken.value}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(options)
      })
      
      if (response.ok) {
        const data = await response.json()
        
        if (data.success && data.data) {
          // Optimistically update local state
          if (balance.value) {
            balance.value.balance = data.data.new_balance
            balance.value.lifetime_spent += options.sparks
          }
          
          // Add the transaction to the list (optimistic)
          const newTransaction: SparkTransaction = {
            id: `temp-${Date.now()}`, // Temporary ID
            user_id: '',
            product: options.product,
            action: options.action,
            sparks_spent: options.sparks,
            balance_after: data.data.new_balance,
            metadata: options.metadata,
            created_at: new Date().toISOString()
          }
          
          transactions.value.unshift(newTransaction)
          
          // Refresh transactions to get the real transaction record
          setTimeout(() => refreshTransactions(), 1000)
          
          return { success: true, newBalance: data.data.new_balance }
        } else {
          error.value = data.error || 'Failed to consume sparks'
          return { success: false, error: error.value }
        }
      } else {
        const errorMessage = `Failed to consume sparks: ${response.status}`
        error.value = errorMessage
        return { success: false, error: errorMessage }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      error.value = errorMessage
      return { success: false, error: errorMessage }
    } finally {
      isLoading.value = false
    }
  }

  async function canAfford(sparks: number): Promise<boolean> {
    if (!balance.value && isInitialized.value) {
      await refreshBalance()
    }
    return currentBalance.value >= sparks
  }

  function startAutoRefresh() {
    if (refreshTimer) return
    
    refreshTimer = setInterval(() => {
      if (isInitialized.value) {
        refreshBalance().catch(console.error)
      }
    }, autoRefreshInterval.value)
  }

  function stopAutoRefresh() {
    if (refreshTimer) {
      clearInterval(refreshTimer)
      refreshTimer = null
    }
  }

  function setAutoRefreshInterval(ms: number) {
    autoRefreshInterval.value = ms
    if (refreshTimer) {
      stopAutoRefresh()
      startAutoRefresh()
    }
  }

  async function fetchPlans() {
    try {
      const response = await fetch(`${apiUrl.value}/subscriptions/plans`)

      if (response.ok) {
        const data = await response.json()
        if (data.success && data.data) {
          plans.value = data.data
        }
      }
    } catch (err) {
      console.error('Failed to fetch plans:', err)
    }
  }

  async function fetchUsage() {
    if (!isInitialized.value) return

    try {
      const response = await fetch(`${apiUrl.value}/subscriptions/usage`, {
        headers: {
          'Authorization': `Bearer ${accessToken.value}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        if (data.success && data.data) {
          subscriptionUsage.value = data.data
        }
      }
    } catch (err) {
      console.error('Failed to fetch usage:', err)
    }
  }

  async function createCheckout(priceId: string): Promise<{ success: boolean; checkout_url?: string; error?: string }> {
    if (!isInitialized.value) throw new Error('Store not initialized')

    try {
      const response = await fetch(`${apiUrl.value}/subscriptions/checkout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken.value}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ price_id: priceId })
      })

      const data = await response.json()
      if (data.success && data.data?.checkout_url) {
        return { success: true, checkout_url: data.data.checkout_url }
      } else {
        return { success: false, error: data.error || 'Failed to create checkout session' }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      return { success: false, error: errorMessage }
    }
  }

  async function createBundleCheckout(priceId: string): Promise<{ success: boolean; checkout_url?: string; error?: string }> {
    if (!isInitialized.value) throw new Error('Store not initialized')

    try {
      const response = await fetch(`${apiUrl.value}/subscriptions/checkout-bundle`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken.value}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ price_id: priceId })
      })

      const data = await response.json()
      if (data.success && data.data?.checkout_url) {
        return { success: true, checkout_url: data.data.checkout_url }
      } else {
        return { success: false, error: data.error || 'Failed to create bundle checkout' }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      return { success: false, error: errorMessage }
    }
  }

  async function openManagePortal(): Promise<{ success: boolean; portal_url?: string; error?: string }> {
    if (!isInitialized.value) throw new Error('Store not initialized')

    try {
      const response = await fetch(`${apiUrl.value}/subscriptions/manage`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken.value}`,
          'Content-Type': 'application/json'
        }
      })

      const data = await response.json()
      if (data.success && data.data?.portal_url) {
        return { success: true, portal_url: data.data.portal_url }
      } else {
        return { success: false, error: data.error || 'Failed to open billing portal' }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      return { success: false, error: errorMessage }
    }
  }

  async function cancelSubscription() {
    if (!isInitialized.value || !activeSubscription.value) {
      throw new Error('No active subscription to cancel')
    }

    isLoading.value = true
    error.value = null

    try {
      const response = await fetch(`${apiUrl.value}/subscriptions/cancel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken.value}`,
          'Content-Type': 'application/json'
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        if (data.success) {
          await refreshSubscription()
          return { success: true }
        } else {
          error.value = data.error || 'Failed to cancel subscription'
          return { success: false, error: error.value }
        }
      } else {
        error.value = `Failed to cancel subscription: ${response.status}`
        return { success: false, error: error.value }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      error.value = errorMessage
      return { success: false, error: errorMessage }
    } finally {
      isLoading.value = false
    }
  }

  async function reactivateSubscription() {
    if (!isInitialized.value || !activeSubscription.value) {
      throw new Error('No subscription to reactivate')
    }

    isLoading.value = true
    error.value = null

    try {
      const response = await fetch(`${apiUrl.value}/subscriptions/reactivate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken.value}`,
          'Content-Type': 'application/json'
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        if (data.success) {
          await refreshSubscription()
          return { success: true }
        } else {
          error.value = data.error || 'Failed to reactivate subscription'
          return { success: false, error: error.value }
        }
      } else {
        error.value = `Failed to reactivate subscription: ${response.status}`
        return { success: false, error: error.value }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      error.value = errorMessage
      return { success: false, error: errorMessage }
    } finally {
      isLoading.value = false
    }
  }

  // Clear all data (useful for logout)
  function clearData() {
    balance.value = null
    transactions.value = []
    activeSubscription.value = null
    plans.value = []
    subscriptionUsage.value = []
    dashboardData.value = null
    error.value = null
    lastRefresh.value = null
    stopAutoRefresh()
  }

  // Get transactions for a specific product
  function getTransactionsByProduct(product: string) {
    return computed(() => 
      transactions.value.filter(t => t.product === product)
    )
  }

  // Get usage stats for a specific time period
  function getUsageStats(days: number = 30) {
    return computed(() => {
      const cutoff = new Date()
      cutoff.setDate(cutoff.getDate() - days)
      
      const filteredTransactions = transactions.value.filter(t => 
        new Date(t.created_at) >= cutoff && t.sparks_spent
      )
      
      const totalSpent = filteredTransactions.reduce((sum, t) => 
        sum + (t.sparks_spent || 0), 0
      )
      
      const productUsage = filteredTransactions.reduce((acc, t) => {
        acc[t.product] = (acc[t.product] || 0) + (t.sparks_spent || 0)
        return acc
      }, {} as Record<string, number>)
      
      return {
        totalSpent,
        transactionCount: filteredTransactions.length,
        productUsage,
        topProduct: Object.entries(productUsage)
          .sort(([,a], [,b]) => b - a)[0]?.[0] || null
      }
    })
  }

  return {
    // State
    apiUrl,
    accessToken,
    balance,
    transactions,
    activeSubscription,
    plans,
    subscriptionUsage,
    dashboardData,
    isLoading,
    error,
    lastRefresh,
    autoRefreshInterval,

    // Computed
    currentBalance,
    lifetimeEarned,
    lifetimeSpent,
    formattedBalance,
    hasActiveSubscription,
    subscriptionWillCancel,
    recentTransactions,
    isInitialized,

    // Actions
    initializeWithAPI,
    refreshBalance,
    refreshTransactions,
    refreshSubscription,
    refreshDashboard,
    refreshAll,
    consumeSparks,
    canAfford,
    startAutoRefresh,
    stopAutoRefresh,
    setAutoRefreshInterval,
    fetchPlans,
    fetchUsage,
    createCheckout,
    createBundleCheckout,
    openManagePortal,
    cancelSubscription,
    reactivateSubscription,
    clearData,
    getTransactionsByProduct,
    getUsageStats
  }
})